import Input from "@components/ui/input";
import cn from "classnames";
import { ClipboardEvent, FC, FormEvent, useRef, useState } from "react";
import Icon from "../Icon";
import Modal from "../Modal";
import ModalContent from "../Modal/ModalContent";
import styles from "./Wallet.module.scss";
import { WalletController } from "@api/WalletController";
import { showToast } from "@utils/showToast";

interface Props {
    isVisible: boolean;
    setIsVisible(isVisible: boolean): void;
}

const Import: FC<Props> = ({ isVisible, setIsVisible }) => {
    const [isCompleted] = useState(false);
    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text");
        if (!text) return;
        const words = text.split(" ");
        ref.current?.querySelectorAll("input").forEach((input, i) => {
            if (!words[i]) input.value = "";
            else input.value = words[i];
        });
    };

    const handleImport = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const words = Array.from(ref.current?.querySelectorAll("input") as NodeListOf<HTMLInputElement>)
                .map((input) => input.value)
                .join(" ");
            const { status } = await WalletController.import(words);
            if (status !== "success") {
                showToast("Invalid secret key or wallet already exists", "error");
                return;
            }
            setIsVisible(false);
            showToast("Wallet imported successfully", "success");
        } catch (error) {
            showToast("Something went wrong", "error");
        }
    };

    const ref = useRef<HTMLDivElement>(null);

    return (
        <Modal isVisible={isVisible} setIsVisible={setIsVisible}>
            <ModalContent classNames={cn(styles.content, { [styles.importContent]: !isCompleted })}>
                {!isCompleted ? (
                    <form onSubmit={handleImport}>
                        <div className={styles.header}>
                            <h2 className={styles.headline}>Import wallet</h2>
                            <button className={styles.close} onClick={() => setIsVisible(false)}>
                                <picture>
                                    <source
                                        srcSet="/images/modal/closemobile.svg"
                                        width={7.5}
                                        height={7.5}
                                        media="(max-width: 768px)"
                                    />
                                    <img
                                        height={12}
                                        width={12}
                                        src="/images/modal/close.svg"
                                        style={{ opacity: 0.4 }}
                                        alt="Close modal"
                                    />
                                </picture>
                            </button>
                        </div>
                        <p className={styles.description}>
                            Enter the secret key of your wallet to import. You don't have to connect your wallet, you
                            can create a new one.
                        </p>
                        <div ref={ref} className={styles.wordsInputs}>
                            {[...new Array(24)].map((_, i) => (
                                <div className={styles.inputHolder} key={i}>
                                    <span>{i + 1}:</span>
                                    <Input required onPaste={handlePaste} name="word" placeholder="" type="text" />
                                </div>
                            ))}
                        </div>
                        <div className={styles.importButtonsHolder}>
                            <button
                                onClick={() => {}}
                                className={cn(styles.button, styles.buttonSecondary, styles.import, styles.buttonCopy)}
                            >
                                <span>Paste</span>
                                <img width={24} height={24} src="/images/profile/copy.svg" alt="" />
                            </button>
                            <button className={cn(styles.button, styles.buttonSecondary, styles.import)}>
                                <span>Import wallet</span>
                                <img width={10} height={10} src="/images/modal/right.svg" alt="" />
                            </button>
                        </div>
                    </form>
                ) : (
                    <>
                        <div className={cn(styles.header, styles.empty)}>
                            <button onClick={() => setIsVisible(false)} className={styles.close}>
                                <img height={12} width={12} src="/images/modal/close.svg" alt="Close modal" />
                            </button>
                        </div>
                        <div className={styles.iconHolder}>
                            <Icon type="Success" />
                        </div>
                        <h2 className={styles.headline}>Perfectly!</h2>
                        <p className={styles.description}>
                            Wallet has been import, the secret key is stored in your personal account!
                        </p>
                        <button onClick={() => setIsVisible(false)} className={styles.buttonSecondary}>
                            <span>Go trade</span>
                            <img width={10} height={10} src="/images/swap/right.svg" alt="" />
                        </button>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};

export default Import;
